// web/src/components/ui.tsx — small shared presentational primitives distilled
// from the house design system: Badge, EmptyState, FeedbackMessage, PanelHeader,
// CodeBlock, ConfirmDialog. Token-only styling. OWNED BY S4.
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import Modal from './Modal';
import { Icon } from './ui/Icon';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export function Badge({
  tone = 'neutral',
  label,
}: {
  tone?: Tone;
  label: React.ReactNode;
}): React.JSX.Element {
  return <span className={`badge badge-${tone}`}>{label}</span>;
}

export function EmptyState({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}): React.JSX.Element {
  return (
    <div
      className="fade-up"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '2rem 1rem',
        textAlign: 'center',
        color: 'var(--color-text2)',
      }}
    >
      <div style={{ fontSize: 'var(--size-h3)' }}>{title}</div>
      {subtitle && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--size-field-label)',
            color: 'var(--color-text3)',
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}

export function PageLoading({ message = 'Loading…' }: { message?: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        background: 'var(--color-bg)',
        color: 'var(--color-text2)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <span className="spinner" aria-hidden="true" style={{ fontSize: '1.5rem' }} />
      <span>{message}</span>
    </div>
  );
}

export function FeedbackMessage({
  ok,
  msg,
}: {
  ok: boolean;
  msg: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={ok ? 'feedback-success' : 'feedback-error'}
      role={ok ? 'status' : 'alert'}
      aria-live={ok ? 'polite' : undefined}
    >
      {msg}
    </div>
  );
}

export function PanelHeader({
  kicker,
  title,
  action,
}: {
  kicker: string;
  title: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1rem 1.1rem',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div>
        <div className="kicker" style={{ marginBottom: '0.2rem' }}>
          {kicker}
        </div>
        <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>{title}</h2>
      </div>
      {action}
    </header>
  );
}

export function CodeBlock({ code }: { code: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <code className="code-block">{code}</code>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          navigator.clipboard?.writeText(code).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            },
            () => undefined,
          );
        }}
        style={{ position: 'absolute', top: '0.4rem', right: '0.4rem' }}
      >
        {copied ? (
          <>
            <Icon icon={Check} size="xs" /> Copied
          </>
        ) : (
          <>
            <Icon icon={Copy} size="xs" /> Copy
          </>
        )}
      </button>
      <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  zIndex,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  zIndex?: number;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <Modal
      title={title}
      maxWidth={420}
      zIndex={zIndex}
      dismissable={!busy}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <span className="spinner" aria-hidden="true" /> : confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--color-text2)', margin: 0 }}>{message}</p>
    </Modal>
  );
}
